/**
 * 🔆 useScreenWakeLock — empêche la mise en veille de l'écran tant que `isActive=true`.
 *
 * Spec : https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
 *
 * Comportement :
 *  - Quand `isActive` passe à `true` → demande un Wake Lock screen.
 *  - Quand `isActive` passe à `false` → release immédiat.
 *  - Quand l'onglet est masqué (`visibilitychange` → hidden), le navigateur
 *    relâche AUTOMATIQUEMENT le Wake Lock. À la réactivation de l'onglet,
 *    on le redemande SI `isActive` est toujours true.
 *  - Au unmount du composant → release garanti.
 *
 * Compatibilité :
 *  - Chrome / Edge desktop + Android : supporté.
 *  - iOS Safari ≥ 16.4 : supporté.
 *  - Si non supporté : `supported=false`, aucune erreur, aucun fallback truqué.
 *
 * Retourne un objet diagnostic léger (utile pour UX/dev) :
 *  { supported, active, error }
 */
import { useEffect, useRef, useState } from 'react';

export default function useScreenWakeLock(isActive) {
  const sentinelRef = useRef(null);
  const [supported] = useState(
    () => typeof navigator !== 'undefined'
      && 'wakeLock' in navigator
      && typeof navigator.wakeLock?.request === 'function'
  );
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supported) return undefined;

    let cancelled = false;

    const acquire = async () => {
      if (sentinelRef.current) return; // déjà actif
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          // Composant a fini avant la résolution — release immédiat.
          try { await sentinel.release(); } catch { /* noop */ }
          return;
        }
        sentinelRef.current = sentinel;
        setActive(true);
        setError(null);
        sentinel.addEventListener('release', () => {
          // Release naturel par le navigateur (ex: tab hidden) ; on met l'état
          // à jour mais on ne ré-acquire pas ici — c'est le rôle du
          // visibilitychange handler ci-dessous.
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
            setActive(false);
          }
        });
      } catch (err) {
        // NotAllowedError (page non visible) ou SecurityError (https requis,
        // permission refusée). On log proprement, pas de fallback truqué.
        setError(err?.name || 'WakeLockError');
        // eslint-disable-next-line no-console
        console.warn('[wake-lock] acquire failed:', err?.name || err?.message);
      }
    };

    const release = async () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      setActive(false);
      if (s) {
        try { await s.release(); } catch { /* noop */ }
      }
    };

    if (isActive) {
      acquire();
    } else {
      release();
    }

    // Quand l'onglet redevient visible et que la démo est toujours active,
    // re-acquérir le Wake Lock (relâché automatiquement par le navigateur).
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && isActive && !sentinelRef.current) {
        acquire();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      release();
    };
  }, [isActive, supported]);

  return { supported, active, error };
}
